import React, { useMemo, useState } from "react";
import FavouriteButton from "./FavouriteButton";

/**
 * ThermalStoreSpecifier.jsx
 *
 * Updates requested:
 * ✅ Add an "Unsure" option for the key questions that shows a short explanation/helper.
 * ✅ Tristar manual fill path: remove/disallow AGA option entirely.
 *
 * Notes:
 * - "Unsure" doesn't block the user. It expands helper text and lets them choose after reading.
 * - Tristar path doesn't even show the AGA/Open-vented boiler checkbox.
 */

const STORE_OPTIONS = {
  TRISTAR_MF_DIRECT: {
    title: "Tristar Manual Fill - Direct (Electric Only)",
    datasheetUrl: "#",
    summary: [
      "No overflow available → only manual fill route.",
      "Direct model (electric-only).",
      "No additional heat source tappings.",
    ],
  },
  TRISTAR_MF_INDIRECT: {
    title: "Tristar Manual Fill - Indirect (Coil for Boiler)",
    datasheetUrl: "#",
    summary: [
      "No overflow available → only manual fill route.",
      "Indirect model with a coil for boiler input.",
      "Still manual fill (no overflow).",
    ],
  },
    TRISTAR_MF_SOLAR: {
    title: "Tristar Manual Fill - Indirect (Solar Coil)",
    datasheetUrl: "#",
    summary: [
      "No overflow available → only manual fill route.",
      "Indirect model with a solar coil.",
      "Includes a single 3kW backup immersion as standard on indirect manual fills.",
    ],
  },


  OV_LOFT_E7: {
    title: "Open-Vented (Header Tank in Loft) - Economy 7 (Electric Only)",
    datasheetUrl: "#",
    summary: [
      "Overflow available.",
      "Header tank in loft.",
      "Economy 7 electric-only (no other tappings).",
    ],
  },
  OV_LOFT_AGA: {
    title: "Open-Vented (Header Tank in Loft) - AGA/OV Boiler Tappings (Std)",
    datasheetUrl: "#",
    summary: [
      "Overflow available.",
      "Header tank in loft.",
      "Includes AGA/OV boiler style connections as standard.",
    ],
  },
  OV_LOFT_AGA_SOLAR: {
    title: "Open-Vented (Header Tank in Loft) - AGA + Solar Coil",
    datasheetUrl: "#",
    summary: ["Overflow available.", "Header tank in loft.", "AGA tappings + solar coil."],
  },
  OV_LOFT_AGA_BOILER: {
    title: "Open-Vented (Header Tank in Loft) - AGA + Boiler",
    datasheetUrl: "#",
    summary: ["Overflow available.", "Header tank in loft.", "AGA tappings + boiler input."],
  },
  OV_LOFT_AGA_BOILER_SOLAR: {
    title: "Open-Vented (Header Tank in Loft) - AGA + Boiler + Solar",
    datasheetUrl: "#",
    summary: [
      "Overflow available.",
      "Header tank in loft.",
      "AGA tappings + boiler + solar.",
    ],
  },

  COMBI_E7: {
    title: "Combination (Header Tank Attached) - Economy 7 (Electric Only)",
    datasheetUrl: "#",
    summary: [
      "Overflow available.",
      "Header tank attached to cylinder.",
      "Economy 7 electric-only (no other tappings).",
    ],
  },
  COMBI_AGA: {
    title: "Combination (Header Tank Attached) - AGA/OV Boiler Tappings (Std)",
    datasheetUrl: "#",
    summary: [
      "Overflow available.",
      "Header tank attached to cylinder.",
      "Includes AGA connections as standard.",
    ],
  },
  COMBI_AGA_SOLAR: {
    title: "Combination (Header Tank Attached) - AGA + Solar Coil",
    datasheetUrl: "#",
    summary: ["Overflow available.", "Header tank attached.", "AGA tappings + solar coil."],
  },
  COMBI_AGA_BOILER: {
    title: "Combination (Header Tank Attached) - AGA + Boiler",
    datasheetUrl: "#",
    summary: ["Overflow available.", "Header tank attached.", "AGA tappings + boiler input."],
  },
  COMBI_AGA_BOILER_SOLAR: {
    title: "Combination (Header Tank Attached) - AGA + Boiler + Solar",
    datasheetUrl: "#",
    summary: ["Overflow available.", "Header tank attached.", "AGA tappings + boiler + solar."],
  },

  SEALED_E7: {
    title: "Sealed System (No Header Tank) - Economy 7 (Electric Only)",
    datasheetUrl: "#",
    summary: [
      "No header tank required.",
      "Sealed system.",
      "Economy 7 electric-only (no other tappings).",
    ],
  },
  SEALED_BOILER: {
    title: "Sealed System (No Header Tank) - Sealed Boiler Coil",
    datasheetUrl: "#",
    summary: ["No header tank required.", "Sealed system.", "Sealed boiler via coil."],
  },
  SEALED_SOLAR: {
    title: "Sealed System (No Header Tank) - Solar Coil",
    datasheetUrl: "#",
    summary: ["No header tank required.", "Sealed system.", "Solar thermal via coil."],
  },
  SEALED_BOILER_SOLAR: {
    title: "Sealed System (No Header Tank) - Sealed Boiler + Solar",
    datasheetUrl: "#",
    summary: [
      "No header tank required.",
      "Sealed system.",
      "Sealed boiler coil + solar coil.",
    ],
  },
};

function resolveOption(facts) {
    if (facts.overflow === "no") {
    // Tristar manual fill: exactly one selection
    // e7 -> direct electric only
    // boiler -> indirect boiler coil
    // solar -> indirect solar coil
    if (facts.tristarMfSource === "e7") return "TRISTAR_MF_DIRECT";
    if (facts.tristarMfSource === "boiler") return "TRISTAR_MF_INDIRECT";
    if (facts.tristarMfSource === "solar") return "TRISTAR_MF_SOLAR";

    // fallback (shouldn't happen if validation is working)
    return null;
  }


  const h = facts.header; // loft | attached | sealed
  const heat = facts.heat || {};

  if (heat.electricOnly) {
    if (h === "loft") return "OV_LOFT_E7";
    if (h === "attached") return "COMBI_E7";
    return "SEALED_E7";
  }

  if (h === "sealed") {
    const hasBoiler = !!heat.sealedBoiler;
    const hasSolar = !!heat.solar;

    if (hasBoiler && hasSolar) return "SEALED_BOILER_SOLAR";
    if (hasBoiler) return "SEALED_BOILER";
    if (hasSolar) return "SEALED_SOLAR";
    return "SEALED_BOILER";
  }

  const hasSolar = !!heat.solar;
  const hasAnyBoiler = !!heat.agaOvBoiler || !!heat.sealedBoiler;

  if (h === "loft") {
    if (hasAnyBoiler && hasSolar) return "OV_LOFT_AGA_BOILER_SOLAR";
    if (hasAnyBoiler) return "OV_LOFT_AGA_BOILER";
    if (hasSolar) return "OV_LOFT_AGA_SOLAR";
    return "OV_LOFT_AGA";
  }

  // attached
  if (hasAnyBoiler && hasSolar) return "COMBI_AGA_BOILER_SOLAR";
  if (hasAnyBoiler) return "COMBI_AGA_BOILER";
  if (hasSolar) return "COMBI_AGA_SOLAR";
  return "COMBI_AGA";
}

function Pill({ children }) {
  return (
    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
      {children}
    </span>
  );
}

function InfoBox({ title, children }) {
  return (
    <div className="mt-3 rounded-md border border-gray-300 bg-gray-50 p-4">
      <div className="text-sm font-semibold text-gray-800">{title}</div>
      <div className="text-sm text-gray-700 mt-1 leading-relaxed">{children}</div>
    </div>
  );
}

function ChoiceButton({ selected, onClick, label, sublabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-md border border-gray-300 hover:bg-gray-50 transition ${
        selected ? "bg-gray-50" : ""
      }`}
    >
      <div className="font-medium text-gray-800">{label}</div>
      {sublabel ? <div className="text-xs text-gray-600 mt-1">{sublabel}</div> : null}
    </button>
  );
}

export default function ThermalStoreSpecifier() {
  const [facts, setFacts] = useState({
    overflow: "", // yes | no
    overflowUnsure: false,

    tristarMfSource: "", // "e7" | "boiler" | "solar" (only for overflow=no)
    tristarMfSourceUnsure: false,


    header: "", // loft | attached | sealed (only for overflow yes)
    headerUnsure: false,

    heat: {
      electricOnly: false,
      agaOvBoiler: false,
      sealedBoiler: false,
      solar: false,
    },
  });

  const [showResult, setShowResult] = useState(false);

  const overflowYes = facts.overflow === "yes";
  const overflowNo = facts.overflow === "no";

  const isTristarPath = overflowNo; // manual fill
  const canChooseHeader = overflowYes;

  const isSealedSystem = facts.header === "sealed";
  const electricOnly = facts.heat.electricOnly;

  const resolvedId = useMemo(() => {
    if (!showResult) return null;

    if (!overflowYes && !overflowNo) return null;
    if (overflowNo && !facts.tristarMfSource) return null;
    if (overflowYes && !facts.header) return null;

    return resolveOption(facts);
  }, [facts, showResult, overflowYes, overflowNo]);

  const resolved = resolvedId ? STORE_OPTIONS[resolvedId] : null;

  const questionsComplete = useMemo(() => {
    if (!overflowYes && !overflowNo) return false;
    if (overflowNo) return !!facts.tristarMfSource;
    if (overflowYes) return !!facts.header;
    return false;
  }, [facts, overflowYes, overflowNo]);

  const reset = () => {
    setFacts({
      overflow: "",
      overflowUnsure: false,
      tristarMfSource: "",
      tristarMfSourceUnsure: false,
      header: "",
      headerUnsure: false,
      heat: { electricOnly: false, agaOvBoiler: false, sealedBoiler: false, solar: false },
    });
    setShowResult(false);
  };

  const setHeat = (key, value) => {
    setFacts((prev) => {
      // Economy 7 electric-only must be exclusive
      if (key === "electricOnly" && value === true) {
        return {
          ...prev,
          heat: { electricOnly: true, agaOvBoiler: false, sealedBoiler: false, solar: false },
        };
      }

      // Any other heat source turns off electricOnly
      if (key !== "electricOnly" && value === true) {
        return { ...prev, heat: { ...prev.heat, electricOnly: false, [key]: true } };
      }

      return { ...prev, heat: { ...prev.heat, [key]: value } };
    });
  };

  const setOverflow = (val) => {
    setFacts((prev) => ({
      ...prev,
      overflow: val,
      // reset dependent answers when switching
      overflowUnsure: false,
      tristarMfSource: val === "no" ? prev.tristarMfSource : "",
      tristarMfSourceUnsure: false,
      header: val === "yes" ? prev.header : "",
      headerUnsure: false,
      heat: { electricOnly: false, agaOvBoiler: false, sealedBoiler: false, solar: false },
    }));
    setShowResult(false);
  };

  const setHeader = (val) => {
    setFacts((prev) => ({
      ...prev,
      header: val,
      headerUnsure: false,
      heat: { electricOnly: false, agaOvBoiler: false, sealedBoiler: false, solar: false },
    }));
    setShowResult(false);
  };

  const setTristarBoiler = (val) => {
    setFacts((prev) => ({ ...prev, tristarBoiler: val, tristarBoilerUnsure: false }));
    setShowResult(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-6">
      <FavouriteButton pageUrl={window.location.pathname} />

      <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-2xl border border-gray-300 mt-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-1">
              Thermal Store Specifier
            </h2>
            <p className="text-sm text-gray-600">
              Answer the questions - it resolves to one thermal store option and shows the datasheet.
            </p>
          </div>

          <button
            onClick={reset}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
            type="button"
          >
            Reset
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Pill>Overflow: {facts.overflow || "-"}</Pill>
          {overflowNo && (
            <Pill>
                Tristar source:{" "}
                {facts.tristarMfSource === "e7"
                ? "Economy 7"
                : facts.tristarMfSource === "boiler"
                ? "Boiler coil"
                : facts.tristarMfSource === "solar"
                ? "Solar coil"
                : "-"}
            </Pill>
            )}

          {overflowYes && <Pill>Header: {facts.header || "-"}</Pill>}
          <Pill>
            Heat:{" "}
            {facts.heat.electricOnly
              ? "Economy 7 Electric Only"
              : [
                  facts.heat.agaOvBoiler ? "OV Boiler" : null,
                  facts.heat.sealedBoiler ? "Sealed Boiler" : null,
                  facts.heat.solar ? "Solar" : null,
                ]
                  .filter(Boolean)
                  .join(", ") || "-"}
          </Pill>
        </div>

        {/* Q1 Overflow */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-800">
            1) Are you able to get an overflow out the property?
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Thermal stores normally need an overflow. Only Tristar Manual Fill does not.
          </p>

          <div className="mt-3 space-y-2">
            <ChoiceButton
              selected={facts.overflow === "yes"}
              onClick={() => setOverflow("yes")}
              label="Yes - overflow is available"
            />
            <ChoiceButton
              selected={facts.overflow === "no"}
              onClick={() => setOverflow("no")}
              label="No - overflow is not possible"
            />
            <ChoiceButton
              selected={facts.overflowUnsure}
              onClick={() =>
                setFacts((prev) => ({
                  ...prev,
                  overflow: "",
                  overflowUnsure: true,
                  tristarMfSource: "",
                  tristarMfSourceUnsure: false,
                  header: "",
                  headerUnsure: false,
                  heat: { electricOnly: false, agaOvBoiler: false, sealedBoiler: false, solar: false },
                }))
              }
              label="Unsure"
              sublabel="Click for an explanation of what we mean by overflow"
            />
          </div>

          {facts.overflowUnsure && (
            <InfoBox title="What does ‘overflow out the property’ mean?">
              The thermal store needs a safe route for an overflow/discharge to a suitable location outside the property.
              In plain terms: can you run a pipe (or have an existing route) that can exit the building safely?
              <br />
              <br />
              If you genuinely cannot route an overflow outside, the only option is the{" "}
              <strong>Tristar Manual Fill</strong>.
              If you <strong>can</strong> route one, you can use the standard open-vented / combination / sealed options.
            </InfoBox>
          )}
        </div>

        {overflowNo && (
        <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800">
            2) Tristar Manual Fill - what heat source is being used?
            </h3>
            <p className="text-sm text-gray-600 mt-1">
            Choose <strong>one</strong>. Tristar manual fill cannot mix sources (no AGA option here).
            Indirect manual fills come with a <strong>single 3kW backup immersion</strong> as standard.
            </p>

            <div className="mt-3 space-y-2">
            <ChoiceButton
                selected={facts.tristarMfSource === "e7"}
                onClick={() =>
                setFacts((prev) => ({
                    ...prev,
                    tristarMfSource: "e7",
                    tristarMfSourceUnsure: false,
                }))
                }
                label="Economy 7 - Electric Only"
                sublabel="No boiler/solar coil. Electric heating only."
            />

            <ChoiceButton
                selected={facts.tristarMfSource === "boiler"}
                onClick={() =>
                setFacts((prev) => ({
                    ...prev,
                    tristarMfSource: "boiler",
                    tristarMfSourceUnsure: false,
                }))
                }
                label="Boiler (Indirect - Coil)"
                sublabel="Boiler heats via a coil (plus 3kW backup immersion)."
            />

            <ChoiceButton
                selected={facts.tristarMfSource === "solar"}
                onClick={() =>
                setFacts((prev) => ({
                    ...prev,
                    tristarMfSource: "solar",
                    tristarMfSourceUnsure: false,
                }))
                }
                label="Solar Thermal (Indirect - Coil)"
                sublabel="Solar heats via a coil (plus 3kW backup immersion)."
            />

            <ChoiceButton
                selected={facts.tristarMfSourceUnsure}
                onClick={() =>
                setFacts((prev) => ({
                    ...prev,
                    tristarMfSource: "",
                    tristarMfSourceUnsure: true,
                }))
                }
                label="Unsure"
                sublabel="Click for a quick explanation to help decide"
            />
            </div>

            {facts.tristarMfSourceUnsure && (
            <InfoBox title="How to pick the Tristar manual fill heat source">
                <strong>Economy 7 (Electric Only)</strong>: they want the store heated purely by immersion(s), typically on off-peak tariff.
                <br />
                <strong>Boiler (Indirect Coil)</strong>: a boiler circuit will heat the store through a coil.
                <br />
                <strong>Solar Thermal (Indirect Coil)</strong>: solar thermal panels feed a coil in the store.
                <br />
                <br />
                Tristar manual fill is <strong>one choice only</strong> here - no mixing heat sources.
            </InfoBox>
            )}
        </div>
        )}


        {/* Q2 Header tank placement (only when overflow = yes) */}
        {canChooseHeader && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800">
              2) If you required a header tank, where would you like it?
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Above/In Loft (Open-Vent) • Attached to cylinder (Combination) • No header tank (Sealed system)
            </p>

            <div className="mt-3 space-y-2">
              <ChoiceButton
                selected={facts.header === "loft"}
                onClick={() => setHeader("loft")}
                label="Above / In loft (Open-vented with header tank)"
              />
              <ChoiceButton
                selected={facts.header === "attached"}
                onClick={() => setHeader("attached")}
                label="Attached to cylinder (Combination thermal store)"
              />
              <ChoiceButton
                selected={facts.header === "sealed"}
                onClick={() => setHeader("sealed")}
                label="No header tank required (Sealed system)"
              />
              <ChoiceButton
                selected={facts.headerUnsure}
                onClick={() =>
                  setFacts((prev) => ({
                    ...prev,
                    header: "",
                    headerUnsure: true,
                    heat: { electricOnly: false, agaOvBoiler: false, sealedBoiler: false, solar: false },
                  }))
                }
                label="Unsure"
                sublabel="Click for a quick explanation of each header option"
              />
            </div>

            {facts.headerUnsure && (
              <InfoBox title="Header tank options explained">
                <strong>Open-vent (header tank in loft/above):</strong> traditional open-vented setup with a separate header tank.
                <br />
                <strong>Combination (header tank attached):</strong> header tank is built onto the cylinder assembly.
                <br />
                <strong>Sealed system (no header tank):</strong> the system is pressurised/sealed; no header tank is used.
                <br />
                <br />
                If they cannot accommodate a loft/header tank, sealed or combination is usually preferred.
              </InfoBox>
            )}
          </div>
        )}

        {/* Q3 Heat sources:
            - Tristar path: NO AGA option at all
            - Sealed system: NO AGA/OV boiler option
            - Economy 7: exclusive
        */}
        {(overflowYes && facts.header) && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800">
              {overflowYes ? "3)" : "3)"} What heat source(s) do you have?
            </h3>

            <p className="text-sm text-gray-600 mt-1">
              Economy 7 (Electric Only) is exclusive (no other tappings).{" "}
              {isTristarPath
                ? "Tristar manual fill does not offer AGA/open-vented boiler connections."
                : isSealedSystem
                ? "Sealed system selected → AGA/open-vented boiler is not available."
                : "If they pick boiler/solar only, you still provide AGA connections as standard (unless Economy 7)."}
            </p>

            <div className="mt-3 space-y-2">
              {/* Economy 7 */}
              <label className="flex items-center gap-3 px-4 py-3 rounded-md border border-gray-300 hover:bg-gray-50 transition cursor-pointer">
                <input
                  type="checkbox"
                  checked={facts.heat.electricOnly}
                  onChange={(e) => setHeat("electricOnly", e.target.checked)}
                  className="h-4 w-4"
                />
                <div>
                  <div className="text-gray-800 font-medium">Economy 7 - Electric Only</div>
                  <div className="text-xs text-gray-600">
                    No tappings for boiler/solar/AGA.
                  </div>
                </div>
              </label>

              {/* AGA / Open-vented boiler:
                  - hidden for Tristar path
                  - hidden for sealed system
              */}
              {!isTristarPath && !isSealedSystem && (
                <label
                  className={`flex items-center gap-3 px-4 py-3 rounded-md border border-gray-300 transition cursor-pointer ${
                    electricOnly ? "opacity-50 pointer-events-none" : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={facts.heat.agaOvBoiler}
                    onChange={(e) => setHeat("agaOvBoiler", e.target.checked)}
                    className="h-4 w-4"
                    disabled={electricOnly}
                  />
                  <div>
                    <div className="text-gray-800 font-medium">AGA / Open-Vented Boiler</div>
                    <div className="text-xs text-gray-600">Open-vented heat source connections.</div>
                  </div>
                </label>
              )}

              {/* Sealed boiler:
                  - allowed for Tristar? Only indirectly matters; keep it available only for overflow YES paths.
                  - For Tristar we only use "boiler present" question to choose direct/indirect.
                  - So for Tristar path, hide boiler/solar toggles to avoid confusion.
              */}
              {!isTristarPath && (
                <>
                  <label
                    className={`flex items-center gap-3 px-4 py-3 rounded-md border border-gray-300 transition cursor-pointer ${
                      electricOnly ? "opacity-50 pointer-events-none" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={facts.heat.sealedBoiler}
                      onChange={(e) => setHeat("sealedBoiler", e.target.checked)}
                      className="h-4 w-4"
                      disabled={electricOnly}
                    />
                    <div>
                      <div className="text-gray-800 font-medium">Sealed System Boiler</div>
                      <div className="text-xs text-gray-600">Requires coil input.</div>
                    </div>
                  </label>

                  <label
                    className={`flex items-center gap-3 px-4 py-3 rounded-md border border-gray-300 transition cursor-pointer ${
                      electricOnly ? "opacity-50 pointer-events-none" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={facts.heat.solar}
                      onChange={(e) => setHeat("solar", e.target.checked)}
                      className="h-4 w-4"
                      disabled={electricOnly}
                    />
                    <div>
                      <div className="text-gray-800 font-medium">Solar Thermal</div>
                      <div className="text-xs text-gray-600">Always via coil.</div>
                    </div>
                  </label>
                </>
              )}

              {/* Unsure helper for heat sources */}
              <ChoiceButton
                selected={facts.heatUnsure === true}
                onClick={() =>
                  setFacts((prev) => ({
                    ...prev,
                    heatUnsure: !prev.heatUnsure,
                  }))
                }
                label={facts.heatUnsure ? "Hide help" : "Unsure"}
                sublabel="Click for guidance on what counts as each heat source"
              />

              {facts.heatUnsure && (
                <InfoBox title="Heat source guidance">
                  <strong>Economy 7 - Electric Only:</strong> they only want immersion heating and do not need any boiler/solar/AGA connections.
                  <br />
                  <strong>Sealed system boiler:</strong> a pressurised boiler circuit heating via a coil.
                  <br />
                  <strong>Solar thermal:</strong> roof solar thermal panels feeding a coil.
                  <br />
                  <br />
                  {(!isTristarPath && !isSealedSystem) ? (
                    <>
                      <strong>AGA / open-vented boiler:</strong> solid fuel / open-vented boiler style input (AGA / stove / vented boiler).
                    </>
                  ) : (
                    <>
                      <strong>AGA/open-vented boiler:</strong> not available for this selection.
                    </>
                  )}
                </InfoBox>
              )}
            </div>
          </div>
        )}

        {/* Resolve button */}
        <button
          onClick={() => setShowResult(true)}
          disabled={!questionsComplete}
          className={`w-full bg-primary text-white py-3 mt-6 rounded-md transition text-lg font-semibold ${
            questionsComplete ? "hover:bg-opacity-90" : "opacity-50 cursor-not-allowed"
          }`}
          type="button"
        >
          Finished
        </button>

        {/* Result */}
        {showResult && (
          <div className="mt-6">
            {!resolved ? (
              <div className="rounded-md border border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
                Missing answers - please answer overflow, and if no overflow, whether a boiler is present.
                If overflow is yes, select a header option.
              </div>
            ) : (
              <div className="rounded-lg border border-gray-300 bg-white p-5">
                <h3 className="text-xl font-semibold text-gray-800">
                  Selected Option: {resolved.title}
                </h3>

                <div className="mt-3">
                  <h4 className="text-sm font-semibold text-gray-700">Why this option</h4>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-700">
                    {resolved.summary.map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <a
                    href={resolved.datasheetUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full sm:w-auto text-center bg-primary text-white px-5 py-3 rounded-md hover:bg-opacity-90 transition font-semibold"
                  >
                    Open Datasheet
                  </a>

                  <button
                    onClick={reset}
                    className="w-full sm:w-auto px-5 py-3 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-semibold"
                    type="button"
                  >
                    Start New Spec
                  </button>
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  Resolved ID: <span className="font-mono">{resolvedId}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="w-full max-w-2xl mt-4 text-xs text-gray-500">
        Edit the store list in <span className="font-mono">STORE_OPTIONS</span> and the mapping in{" "}
        <span className="font-mono">resolveOption()</span>.
      </div>
    </div>
  );
}
